import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { BestOf2025Gradient } from 'app/components/BestOf2025/styled';
import {
  ResourceSubmissionModalWarningButton,
  ResourceSubmissionModalWarningButtonCancel,
  ResourceSubmissionModalWarningButtonsContainer,
  ResourceSubmissionModalWarningButtonText,
  ResourceSubmissionModalWarningText,
  ResourceSubmissionModalWarningWrapper,
} from 'app/components/ResourceSubmissionModal/styled';
import { IResource } from 'app/components/Resources/ResourceCard';
import ResourceDetail from 'app/components/Resources/ResourceDetail';
import { CloseIcon } from 'app/components/shared/icons';
import useOnClickOutside from 'hooks/click-outside';
import useIsMobile from 'hooks/useIsMobile';
import { bestOfVotingSelector } from 'store/reducers/best-of/best-of-reducer';
import {
  tiptapEditorFocusSelector,
  dialogOpenSelector,
  IGlobalStateResourceModal,
} from 'store/reducers/global/global-reducer';
import {
  isDrawerOpenSelector,
  isResourceFullscreenOpenSelector,
} from 'store/reducers/navbar-reducer';
import { ThemeProvider } from 'styled-components';

import { ResourceModalContext } from './ResourceModalContext';
import {
  ResourceModalCloseIcon,
  ResourceModalContainer,
  ResourceModalContent,
  ResourceModalWrapper,
} from './styled';

interface ResourceModalProps {
  onClose?: () => void;
  existingResource?: IResource;
  enableExistingResourceInteractions?: boolean;
  enableExistingResourceComments?: boolean;
  hideComments?: boolean;
  preventShowEditing?: boolean;
  forceBestOfTheme?: boolean;
  ariaLabel?: string;
  voteAction?: React.ReactNode;
  footer?: React.ReactNode;
  renderEditing?: (onBack: () => void) => React.ReactNode;
  resourceModal: IGlobalStateResourceModal;
}

export default function ResourceModal({
  onClose,
  resourceModal,
  existingResource,
  enableExistingResourceInteractions = false,
  enableExistingResourceComments = false,
  hideComments = false,
  preventShowEditing = false,
  forceBestOfTheme = false,
  ariaLabel = 'Resource details',
  voteAction,
  footer,
  renderEditing,
}: ResourceModalProps) {
  const isMobile = useIsMobile();
  const reduxIsVoting = useSelector(bestOfVotingSelector);
  const isVoting = forceBestOfTheme || reduxIsVoting;
  const tiptapEditorFocus = useSelector(tiptapEditorFocusSelector);
  const resourceModalWrapperRef = useRef<HTMLDivElement>(null);
  const isDrawerOpen = useSelector(isDrawerOpenSelector);
  const isResourceFullscreenOpen = useSelector(isResourceFullscreenOpenSelector);
  const isDialogOpen = useSelector(dialogOpenSelector);
  const [isDrawerClosing, setIsDrawerClosing] = useState<boolean>(true);
  const [isEmbedExpanded, setIsEmbedExpanded] = useState<boolean>(false);
  const [shouldUseLayoutId, setShouldUseLayoutId] = useState<boolean>(true);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [pendingReturn, setPendingReturn] = useState<(() => void) | null>(null);

  useEffect(() => {
    setShouldUseLayoutId(true);
  }, [resourceModal.slug, resourceModal.unique_id]);

  useEffect(() => {
    if (isDrawerOpen) {
      setIsDrawerClosing(false);
    }
  }, [isDrawerOpen]);

  const handleCloseModalClick = useCallback(() => {
    if (showWarning) return;
    if (isEditing) {
      setPendingReturn(null);
      setShowWarning(true);
      return;
    }

    onClose?.();
  }, [isEditing, onClose, showWarning]);

  const requestReturnConfirmation = useCallback((onConfirm: () => void) => {
    setPendingReturn(() => onConfirm);
    setShowWarning(true);
  }, []);

  useEffect(() => {
    const closeButton = resourceModalWrapperRef.current?.querySelector<HTMLButtonElement>(
      'button[data-resource-modal-close]'
    );
    closeButton?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isResourceFullscreenOpen) {
        event.preventDefault();
        handleCloseModalClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCloseModalClick, isResourceFullscreenOpen]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'drawers:closing') {
        setIsDrawerClosing(true);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const wrapperVariants = {
    drawerOpen: {
      padding: '0px 536px 0px 16px',
    },
    drawerClosed: {
      padding: '0px 16px 0px 16px',
    },
  };

  const contentVariants = {
    embedExpanded: {
      maxWidth: '100vw',
      height: '75rem',
      maxHeight: 'calc(100svh - (var(--navbar-height) * 2))',
      opacity: 1,
    },
    mobileEmbedExpanded: {
      maxWidth: '100vw',
      height: '35rem',
      opacity: 1,
    },
    embedCollapsed: {
      maxWidth: '90rem',
      opacity: 1,
    },
  };
  useOnClickOutside(resourceModalWrapperRef, (e) => {
    if ((e.target as HTMLElement).closest?.('[data-resource-fullscreen]')) return;
    if (
      isResourceFullscreenOpen ||
      isDrawerOpen ||
      tiptapEditorFocus ||
      isDialogOpen ||
      showWarning
    )
      return;
    // Closing on mousedown must not let its default focus action override
    // the opener focus restored when the modal unmounts.
    if (e.type === 'mousedown') e.preventDefault();
    handleCloseModalClick();
  });

  const handleCloseModal = () => {
    onClose?.();
  };

  const handleEmbedExpanded = () => {
    setIsEmbedExpanded(true);
  };

  const handleEmbedCollapsed = () => {
    setIsEmbedExpanded(false);
  };

  const handleConfirmClose = () => {
    if (pendingReturn) {
      setShowWarning(false);
      setPendingReturn(null);
      pendingReturn();
    } else {
      handleCloseModal();
    }
  };

  const handleConfirmCancelClose = () => {
    setPendingReturn(null);
    setShowWarning(false);
  };

  return (
    <ThemeProvider
      theme={{
        isVoting,
      }}
    >
      <ResourceModalContext.Provider
        value={{ showWarning, setShowWarning, isEditing, setIsEditing, requestReturnConfirmation }}
      >
        <ResourceModalWrapper
          data-id="resource-modal-wrapper"
          variants={wrapperVariants}
          animate={!isDrawerClosing ? 'drawerOpen' : 'drawerClosed'}
          transition={forceBestOfTheme ? { type: 'tween', duration: 0 } : undefined}
        >
          <ResourceModalContainer
            ref={resourceModalWrapperRef}
            {...(shouldUseLayoutId && !forceBestOfTheme
              ? {
                  layoutId: resourceModal?.unique_id
                    ? `resource-card-${resourceModal?.slug}-${resourceModal?.unique_id}`
                    : `resource-card-${resourceModal?.slug}`,
                }
              : {})}
            onLayoutAnimationComplete={() => {
              if (shouldUseLayoutId) {
                setShouldUseLayoutId(false);
              }
            }}
            data-id="resource-modal-container"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            variants={contentVariants}
            showingWarning={showWarning}
            initial={
              forceBestOfTheme ? false : { opacity: 0, maxWidth: '90rem', scale: 0.995, y: 6 }
            }
            transition={forceBestOfTheme ? { type: 'tween', duration: 0 } : undefined}
            animate={
              isEmbedExpanded
                ? isMobile
                  ? 'mobileEmbedExpanded'
                  : 'embedExpanded'
                : 'embedCollapsed'
            }
          >
            {isVoting ? <BestOf2025Gradient /> : null}
            {isMobile && isEmbedExpanded ? null : (
              <ResourceModalCloseIcon
                as="button"
                type="button"
                aria-label="Close resource modal"
                aria-labelledby="resource-modal-close-label"
                data-resource-modal-close
                onClick={handleCloseModalClick}
              >
                <span
                  id="resource-modal-close-label"
                  style={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                  }}
                >
                  Close resource
                </span>
                <CloseIcon width={18} height={18} fill="var(--color-primary)" fillOpacity="1" />
              </ResourceModalCloseIcon>
            )}
            <ResourceModalContent>
              <Suspense fallback={null}>
                <ResourceDetail
                  slug={resourceModal.slug}
                  existingResource={existingResource}
                  enableExistingResourceInteractions={enableExistingResourceInteractions}
                  enableExistingResourceComments={enableExistingResourceComments}
                  hideComments={hideComments}
                  preventShowEditing={preventShowEditing}
                  renderEditing={renderEditing}
                  voteAction={voteAction}
                  onEmbedExpanded={handleEmbedExpanded}
                  onEmbedCollapsed={handleEmbedCollapsed}
                />
                {footer}
              </Suspense>
            </ResourceModalContent>
          </ResourceModalContainer>
          {showWarning ? (
            <>
              <ResourceSubmissionModalWarningWrapper
                role="alertdialog"
                aria-modal="true"
                aria-label="Unsaved changes"
                style={forceBestOfTheme ? { animation: 'none' } : undefined}
              >
                <ResourceSubmissionModalWarningText>
                  Changes you made may not be saved. Are you sure you want to close this modal?
                </ResourceSubmissionModalWarningText>
                <ResourceSubmissionModalWarningButtonsContainer>
                  <ResourceSubmissionModalWarningButton onClick={handleConfirmClose}>
                    <ResourceSubmissionModalWarningButtonText>
                      Yes
                    </ResourceSubmissionModalWarningButtonText>
                  </ResourceSubmissionModalWarningButton>
                  <ResourceSubmissionModalWarningButtonCancel onClick={handleConfirmCancelClose}>
                    <ResourceSubmissionModalWarningButtonText>
                      No
                    </ResourceSubmissionModalWarningButtonText>
                  </ResourceSubmissionModalWarningButtonCancel>
                </ResourceSubmissionModalWarningButtonsContainer>
              </ResourceSubmissionModalWarningWrapper>
            </>
          ) : null}
        </ResourceModalWrapper>
      </ResourceModalContext.Provider>
    </ThemeProvider>
  );
}
